import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { useAuth } from "../../../context/AuthContext";
import {
    clearAddressState,
    clearCurrentAddress,
    getAllAddresses,
} from "../../../Redux/customer-address/slice";
import { CreateAddressRequest } from "../../../Redux/customer-address/types";
import {
    confirmDeliveryLocation,
    setDeliveryLocation,
} from "../../../Redux/delivery-location/slice";
import {
    ResolvedLocationDetails,
    reverseGeocodeCoordinates,
} from "../../../utility/location";
import AddNewAddress from "../../../user/pages/manageaddress/AddNewAddress";
import "../../../user/pages/manageaddress/ManageAddressPage.css";
import LocationSetupModal from "./LocationSetupModal";
import {
    ADDRESS_SETUP_EVENT,
    LOCATION_GATE_SESSION_KEY,
} from "./LocationSetupData";
import { DeliveryLocation } from "./LocationSetupTypes";

type GateStep = "idle" | "location" | "details";

const AddressSetupGate = () => {
    const dispatch = useAppDispatch();
    const { isAuthenticated, profileLoading, user } = useAuth();
    const { addresses } = useAppSelector((state) => state.customerAddress);
    const [step, setStep] = useState<GateStep>("idle");
    const [initialValues, setInitialValues] =
        useState<Partial<CreateAddressRequest> | null>(null);
    const checkedRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated) {
            checkedRef.current = false;
            setStep("idle");
            return;
        }
        if (profileLoading || checkedRef.current) return;
        if (sessionStorage.getItem(LOCATION_GATE_SESSION_KEY) !== "1") return;

        checkedRef.current = true;
        dispatch(getAllAddresses()).then((action) => {
            if (getAllAddresses.fulfilled.match(action)) {
                const data = action.payload.data;
                const count = Array.isArray(data) ? data.length : data ? 1 : 0;
                if (count === 0) {
                    setStep("location");
                } else {
                    sessionStorage.removeItem(LOCATION_GATE_SESSION_KEY);
                }
            } else {
                checkedRef.current = false;
            }
        });
    }, [dispatch, isAuthenticated, profileLoading]);

    // Manual trigger — e.g. navbar "Deliver to" with no saved addresses.
    useEffect(() => {
        const openFlow = () =>
            setStep((current) => (current === "idle" ? "location" : current));
        window.addEventListener(ADDRESS_SETUP_EVENT, openFlow);
        return () => window.removeEventListener(ADDRESS_SETUP_EVENT, openFlow);
    }, []);

    const handleLocationConfirm = async (location: DeliveryLocation) => {
        let resolved: ResolvedLocationDetails | null = null;
        try {
            resolved = await reverseGeocodeCoordinates(
                location.latitude,
                location.longitude
            );
        } catch {
            resolved = null;
        }

        // Sync the delivery-location slice so the picked point also becomes
        // the site-wide delivery location.
        dispatch(
            setDeliveryLocation({
                label: location.label,
                area: resolved?.area || undefined,
                city: resolved?.city || undefined,
                state: resolved?.state || undefined,
                pincode: resolved?.pincode || undefined,
                displayName:
                    resolved?.displayName || location.address || location.label,
                latitude: location.latitude,
                longitude: location.longitude,
            })
        );
        dispatch(confirmDeliveryLocation());

        // Guests cannot hit the address API — the picked point still becomes
        // their delivery location, but there is nothing to save.
        if (!isAuthenticated) {
            setStep("idle");
            return;
        }

        // Ensure AddNewAddress mounts in "add" mode, not a stale edit session,
        // and that no leftover success flag fires its onSuccess immediately.
        dispatch(clearCurrentAddress());
        dispatch(clearAddressState());

        setInitialValues({
            fullName: user?.fullName || user?.name || "",
            email: user?.emailId || user?.email || "",
            phoneNumber: (user?.phone || "").replace(/\D/g, "").slice(-10),
            country: resolved?.country || "India",
            state: resolved?.state || "",
            district: resolved?.district || "",
            city: resolved?.city || "",
            pincode: resolved?.pincode || "",
            addressLine1: resolved?.area || "",
            latitude: location.latitude,
            longitude: location.longitude,
            addressType: "Home",
            defaultAddress: true,
        });
        setStep("details");
    };

    const handleSaved = () => {
        sessionStorage.removeItem(LOCATION_GATE_SESSION_KEY);
        // addAddress.fulfilled assigns payload.data directly — refetch so the
        // list is guaranteed to be the full array, whatever the API returns.
        dispatch(getAllAddresses());
        setStep("idle");
    };

    // Closable only when the user already has a saved address — with none,
    // the flow stays forced so a deliverable address is always collected.
    const hasAddresses = (addresses?.length ?? 0) > 0;
    const handleModalClose = hasAddresses ? () => setStep("idle") : undefined;

    return (
        <>
            <LocationSetupModal
                open={step === "location"}
                onConfirm={handleLocationConfirm}
                onClose={handleModalClose}
            />
            {step === "details" && initialValues && (
                <div className="location-setup-backdrop" role="presentation">
                    <section
                        className="location-setup-modal location-setup-modal--form"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Add your delivery address"
                    >
                        <AddNewAddress
                            initialValues={initialValues}
                            onSuccess={handleSaved}
                            onClose={() =>
                                setStep(hasAddresses ? "idle" : "location")
                            }
                        />
                    </section>
                </div>
            )}
        </>
    );
};

export default AddressSetupGate;
